import { useNavigate, Outlet, Navigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { RootState } from '~/store/store';
import useGetUserRole from '~/hooks/useGetUserRole';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { hasSupervisorInfoRouteAccess, isRouteRestricted, isSupervisorControlledCovenantsRoute, resolveSignInPathByRoute } from './utils';
import { IdentificationType } from '../identification/forms/GettingStarted/types';
import { IS_AUTHENTICATED_KEY } from './constants';
import { fetchSupervisorInfo } from '~/store/users/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch';
import {
  selectSupervisorCovenantsRights,
  selectIsSupervisorInfoLoading,
  selectShouldFetchSupervisorInfo,
} from '~/store/users/selectors.ts';

const ProtectedRoute = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    token,
    loading: isLoading,
    isInitialized,
  } = useSelector((state: RootState) => state.users) || {};
  const covenantsRights = useSelector(selectSupervisorCovenantsRights);
  const isSupervisorInfoLoading = useSelector(selectIsSupervisorInfoLoading);
  const shouldFetchSupervisorInfo = useSelector(selectShouldFetchSupervisorInfo);
  const { isClientLogin, isAssetManager, isManagerLogin, roles } = useGetUserRole();

  const { pathname, search } = useLocation();
  const isAuthorized = localStorage.getItem(IS_AUTHENTICATED_KEY);
  const isSupervisorRoute = isSupervisorControlledCovenantsRoute(pathname);
  const hasSupervisorRouteAccess = hasSupervisorInfoRouteAccess(pathname, covenantsRights);
  const isRestricted = isRouteRestricted(pathname, token, roles, covenantsRights);
  const shouldBootstrapSupervisorInfo =
    !!token &&
    isSupervisorRoute &&
    isRestricted &&
    (shouldFetchSupervisorInfo || isSupervisorInfoLoading);
  const shouldShowInitialLoader =
    ((!isInitialized || (isLoading && !token)) && isAuthorized) ||
    shouldBootstrapSupervisorInfo;
  const canRenderOutlet =
    isClientLogin ||
    isManagerLogin ||
    (!!token && isSupervisorRoute && hasSupervisorRouteAccess);

  useEffect(() => {
    if (isAssetManager) {
      navigate(`/identification/questionnaire?type=${IdentificationType.PRIMARY}`);
    }
  }, [token, navigate, isAssetManager]);

  useEffect(() => {
    if (!shouldBootstrapSupervisorInfo || !shouldFetchSupervisorInfo) {
      return;
    }
    void dispatch(fetchSupervisorInfo({ token }));
  }, [dispatch, shouldBootstrapSupervisorInfo, shouldFetchSupervisorInfo, token]);

  if (shouldShowInitialLoader) {
    return <FullPageLoader />;
  }

  if (isRestricted) {
    const signInPath = resolveSignInPathByRoute(pathname);
    const redirectPath = `${pathname}${search}`;

    return <Navigate to={signInPath} state={{ from: redirectPath }} replace />;
  }

  if (canRenderOutlet) {
    return <Outlet />;
  }

  return null;
};

export default ProtectedRoute;
